import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Globe2, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { HKBadge } from "@/components/site/HKBadge";
import { StatsCounter } from "@/components/site/StatsCounter";
import { PartnerMarquee } from "@/components/site/PartnerMarquee";
import { SectionHeading } from "@/components/site/SectionHeading";
import { CTASection } from "@/components/site/CTASection";
import { DashboardShowcase } from "@/components/site/DashboardShowcase";
import { SupportedChannelsSection, PaymentMethodsSection } from "@/components/site/SupportedChannels";
import {
  DASHBOARD_FEATURES, CORE_ADVANTAGES, REGIONS, INDUSTRIES, WHY_DC_REASONS, INSTITUTIONAL_PROOFS, BRAND,
} from "@/lib/content";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Seo } from "@/components/site/Seo";

const Hero = () => {
  const { t } = useTranslation();
  const pills = [
    t("home.heroPill1", { returnObjects: true }),
    t("home.heroPill2", { returnObjects: true }),
    t("home.heroPill3", { returnObjects: true }),
  ];
  return (
    <section data-testid="home-hero" className="relative overflow-hidden">
      <div className="absolute inset-0 dc-dot-grid opacity-60" aria-hidden />
      <div className="absolute inset-0 dc-soft-radial" aria-hidden />
      <div className="relative mx-auto w-full max-w-6xl px-4 pb-12 pt-14 sm:px-6 sm:pb-16 sm:pt-20 lg:px-8">
        {/* Two-column split on desktop (≥lg): image left ~45%, text right ~55%.
            On mobile the image stacks on top so the hero still feels visual
            above the fold; text below drives the pitch. */}
        <div className="grid items-center gap-10 lg:grid-cols-[45%_1fr] lg:gap-12">
          {/* --- Left column: hero image --- */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, ease: [0.2, 0.7, 0.2, 1] }}
            className="relative order-first mx-auto w-full max-w-md lg:max-w-none"
            data-testid="home-hero-visual"
          >
            <div className="relative aspect-square overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200/70 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.18)]">
              <picture>
                <source srcSet="/screenshots/og-card-square.webp" type="image/webp" />
                <img
                  src="/screenshots/og-card-square.webp"
                  alt={t("home.heroImageAlt", "Danny Clans DC Dashboard — one wallet, every ad platform: Meta, Google, TikTok and more.")}
                  width="800"
                  height="800"
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-contain"
                  data-testid="home-hero-image"
                />
              </picture>
            </div>
          </motion.div>

          {/* --- Right column: text + CTAs + trust pills --- */}
          <div className="text-left">
            <HKBadge />
            <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="mt-6 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-[54px] lg:leading-[1.05]">
              {t("home.heroTitle")}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {t("home.heroSubtitle")}
            </motion.p>
            <p className="mt-3 text-xs font-bold uppercase tracking-[0.22em] text-blue-700">{BRAND.marketingLine}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/contact" data-testid="hero-primary-cta-button">
                <Button className="h-11 rounded-lg bg-blue-600 px-5 text-white hover:bg-blue-700">
                  {t("cta.primary")} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/dashboard" data-testid="hero-secondary-cta-button">
                <Button variant="outline" className="h-11 rounded-lg border-slate-300 bg-white px-5 text-slate-900 hover:bg-slate-50">
                  {t("cta.secondary")}
                </Button>
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
              {pills.map((p) => (
                <div key={p.title} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{p.title}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <StatsCounter />
      </div>
    </section>
  );
};

const DashboardCapabilities = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="growth-engine-section">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading eyebrow={t("sections.growthEngine.eyebrow")} title={t("sections.growthEngine.title")} description={t("sections.growthEngine.desc")} align="center" testIdPrefix="growth-engine" />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {DASHBOARD_FEATURES.map((m, idx) => {
            const code = t(`content.${m.tk}.code`);
            const title = t(`content.${m.tk}.title`);
            const description = t(`content.${m.tk}.description`);
            const bullets = t(`content.${m.tk}.bullets`, { returnObjects: true }) || [];
            return (
              <motion.div key={m.key} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.35, delay: idx * 0.04 }}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)]"
                data-testid={`growth-module-card-${m.key}`}>
                <span className="absolute inset-x-0 top-0 h-1 bg-blue-600" />
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-50 text-blue-700"><m.icon className="h-6 w-6" /></div>
                <div className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-blue-700">{code}</div>
                <h3 className="mt-1 text-lg font-extrabold tracking-tight text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
                <ul className="mt-4 space-y-1.5">
                  {bullets.map((b) => (<li key={b} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600" />{b}</li>))}
                </ul>
              </motion.div>
            );
          })}
        </div>
        <div className="mt-8 flex justify-center">
          <Link to="/dashboard" data-testid="growth-engine-explore-link">
            <Button variant="outline" className="h-11 rounded-lg border-slate-300 bg-white px-5 text-slate-900 hover:bg-slate-50">
              {t("cta.viewDetails")} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const CoreAdvantages = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="core-advantages-section" className="bg-slate-50">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading eyebrow={t("sections.advantages.eyebrow")} title={t("sections.advantages.title")} description={t("sections.advantages.desc")} testIdPrefix="core-advantages" />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {CORE_ADVANTAGES.map((a, idx) => (
            <motion.div key={a.key} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.35, delay: idx * 0.04 }} className="rounded-xl border border-slate-200 bg-white p-6 hover:border-slate-300" data-testid={`core-advantage-card-${idx}`}>
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-blue-700"><a.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 text-base font-bold text-slate-900">{t(`content.${a.tk}.title`)}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{t(`content.${a.tk}.description`)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const RegionalSolutions = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="regional-solutions-section">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading eyebrow={t("sections.regional.eyebrow")} title={t("sections.regional.title")} description={t("sections.regional.desc")} testIdPrefix="regional-solutions" />
        <Tabs defaultValue={REGIONS[0].key} className="mt-8">
          <TabsList data-testid="localization-region-tabs" className="flex h-auto w-full flex-wrap justify-start gap-2 rounded-xl bg-slate-50 p-2 sm:w-auto sm:inline-flex">
            {REGIONS.map((r) => (
              <TabsTrigger key={r.key} value={r.key} data-testid={`region-tab-${r.key}`} className="rounded-lg px-3 py-1.5 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
                {t(`content.${r.tk}.label`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {REGIONS.map((r) => {
            const markets = t(`content.${r.tk}.markets`, { returnObjects: true }) || [];
            const playbook = t(`content.${r.tk}.playbook`, { returnObjects: true }) || [];
            return (
              <TabsContent key={r.key} value={r.key} data-testid={`region-content-${r.key}`} className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800"><Globe2 className="h-4 w-4" /> {t(`content.${r.tk}.label`)}</div>
                    <h3 className="mt-3 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t(`content.${r.tk}.headline`)}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{t(`content.${r.tk}.summary`)}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {markets.map((m) => (<span key={m} className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">{m}</span>))}
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.primaryChannels")}</div>
                      <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-slate-800">
                        {r.channels.map((c) => (<li key={c} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-600" />{c}</li>))}
                      </ul>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.localPlaybook")}</div>
                      <ul className="mt-3 space-y-2 text-sm text-slate-800">
                        {playbook.map((p) => (<li key={p} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{p}</li>))}
                      </ul>
                    </div>
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </section>
  );
};

const IndustrySolutions = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="industry-solutions-section" className="bg-slate-50">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading eyebrow={t("sections.industry.eyebrow")} title={t("sections.industry.title")} description={t("sections.industry.desc")} testIdPrefix="industry-solutions" />
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {INDUSTRIES.map((ind, idx) => {
            const bullets = t(`content.${ind.tk}.bullets`, { returnObjects: true }) || [];
            return (
              <motion.div key={ind.key} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.35, delay: idx * 0.04 }} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 sm:p-7" data-testid={`industry-card-${ind.key}`}>
                <div className="flex items-start gap-4">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-blue-50 text-blue-700"><ind.icon className="h-6 w-6" /></div>
                  <div>
                    <h3 className="text-lg font-extrabold tracking-tight text-slate-900">{t(`content.${ind.tk}.label`)}</h3>
                    <p className="mt-1 text-sm font-semibold text-slate-700">{t(`content.${ind.tk}.headline`)}</p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(`content.${ind.tk}.summary`)}</p>
                    <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {bullets.map((b) => (<li key={b} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {b}</li>))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const InstitutionalProof = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="institutional-proof-section">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading eyebrow={t("sections.institutional.eyebrow")} title={t("sections.institutional.title")} description={t("sections.institutional.desc")} align="center" testIdPrefix="institutional-proof" />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {INSTITUTIONAL_PROOFS.map((p) => (
            <div key={p.key} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`institutional-proof-card-${p.key}`}>
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-50 text-emerald-700"><p.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 text-base font-bold text-slate-900">{t(`content.${p.tk}.title`)}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{t(`content.${p.tk}.description`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyDannyClans = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="why-danny-clans-section" className="bg-slate-50">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading eyebrow={t("sections.why.eyebrow")} title={t("sections.why.title")} description={t("sections.why.desc")} align="center" testIdPrefix="why-danny-clans" />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {WHY_DC_REASONS.map((r, idx) => (
            <motion.div key={r.key} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.35, delay: idx * 0.04 }} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`why-card-${idx}`}>
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-blue-700"><r.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 text-base font-bold text-slate-900">{t(`content.${r.tk}.title`)}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{t(`content.${r.tk}.description`)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function HomePage() {
  const { t } = useTranslation();
  return (
    <div data-testid="home-page">
      <Seo
        title={t("seo.home.metaTitle")}
        description={t("seo.home.metaDescription")}
        keywords={t("seo.home.keywords")}
        ogTitle={t("seo.home.ogTitle")}
        ogDescription={t("seo.home.ogDescription")}
        imageAlt={t("seo.home.imageAlt")}
        path="/"
      />
      <Hero />
      <PartnerMarquee />
      <DashboardShowcase />
      <DashboardCapabilities />
      <SupportedChannelsSection />
      <PaymentMethodsSection />
      <CoreAdvantages />
      <RegionalSolutions />
      <IndustrySolutions />
      <InstitutionalProof />
      <WhyDannyClans />
      <CTASection />
    </div>
  );
}
